import { ParseKeys } from '@if/core/i18n'
import { EPaymentMethod } from '@if/core/types'
import { ErrorAlert } from '@if/mui'
import { Typography } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../Common/Dialog/FormDialog'

interface LoanCalendarResetDialogProps {
  originalSignedSimulation: SignedCalendarSimulation
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}

const LoanCalendarResetDialog = ({
  originalSignedSimulation,
  onConfirm,
  onClose,
  isLockedByOther = false,
}: LoanCalendarResetDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    originalSignedSimulation.simulation.fullLoan.loan.id,
    originalSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const handleConfirm = () => {
    void computeAction({
      ...originalSignedSimulation,
      action: {
        actionType: ECalendarSimulationAction.Reset,
      },
    }).then((result) => {
      onConfirm(result)
      closeDialog()
    })
  }

  const errorMessageKey = useMemo((): ParseKeys | undefined => {
    if (originalSignedSimulation.simulation.actions.length !== 0)
      return 'loan.payment.resetDialog.errors.hasPreviousActions'

    const expectedPayments = originalSignedSimulation.simulation.fullLoan.expectedPaymentsCalendar.expectedPayments
    const lastExpectedPaymentDate = expectedPayments?.length
      ? expectedPayments[expectedPayments.length - 1].date
      : undefined

    const minimumDate = originalSignedSimulation.simulation.getNextValidActionDate(
      EPaymentMethod.PreAuthorizedPayment,
      canBypassAcpLock,
    )

    // Last expected payment has been made or is in the scope of the ACP processing.
    if (!lastExpectedPaymentDate || lastExpectedPaymentDate < minimumDate)
      return 'loan.payment.resetDialog.errors.pastLastExpectedPaymentDate'
    if (originalSignedSimulation.simulation.hasExecutedPaymentsAfterDate(minimumDate))
      return 'loan.payment.resetDialog.errors.hasCompletedPaymentInFutur'
  }, [originalSignedSimulation, canBypassAcpLock])

  return (
    <FormDialog
      {...dialogProps}
      title={t('loan.payment.reset')}
      onConfirm={handleConfirm}
      loading={isPending}
      disabled={Boolean(errorMessageKey) || isLockedByOther}
    >
      {error && <ErrorAlert error={error} />}
      {errorMessageKey && <ErrorAlert error={t(errorMessageKey)} />}
      <Typography>{t('loan.payment.resetConfirm')}</Typography>
    </FormDialog>
  )
}

export default LoanCalendarResetDialog
